<template>
	<div class="glp1">
		<header class="glp1__nav">
			<inline-svg :src="logoSvg" class="glp1__logo" aria-label="Simple" />
			<button
				type="button"
				class="btn-black glp1-cta glp1-cta--nav"
				@click="handleCtaClick($event, 'ButtonClick', 'GET_STARTED', 'GET_STARTED_HEADER')"
			>
				Get started
			</button>
		</header>

		<main class="glp1__content container-fluid">
			<BlockVisibilityObserver title="LANDING_WEIGHT_LOSS_GOAL" :threshold="1">
				<section class="glp1-quiz">
					<p class="glp1-quiz__subtitle glp1-gradient-text">See if you qualify for weight loss medication</p>
					<h2 class="glp1-quiz__title">What's your weight loss goal?</h2>
					<div class="glp1-quiz__options">
						<button
							v-for="option in weightLossOptions"
							:key="option.value"
							type="button"
							class="glp1-quiz__option"
							@click="handleWeightLossAnswer(option.value)"
						>
							{{ option.label }}
						</button>
					</div>
				</section>
			</BlockVisibilityObserver>

			<section class="glp1-hero">
				<div class="glp1-hero__copy">
					<div class="glp1-pill">Starting at $99/mo</div>
					<h1 class="glp1-hero__title">
						Lose weight with a
						<span class="glp1-gradient-text">personalized GLP-1 plan</span>
						, done the Simple way
					</h1>
					<ul class="glp1-hero__list">
						<li class="glp1-hero__item">
							<span class="glp1-hero__check">
								<inline-svg :src="checkSvg" aria-hidden="true" />
							</span>
							Access to GLP-1 medications, if eligible*
						</li>
						<li class="glp1-hero__item">
							<span class="glp1-hero__check">
								<inline-svg :src="checkSvg" aria-hidden="true" />
							</span>
							No insurance required + free shipping
						</li>
						<li class="glp1-hero__item">
							<span class="glp1-hero__check">
								<inline-svg :src="checkSvg" aria-hidden="true" />
							</span>
							Ongoing support from licensed clinicians**
						</li>
						<li class="glp1-hero__item">
							<span class="glp1-hero__check">
								<inline-svg :src="checkSvg" aria-hidden="true" />
							</span>
							HSA/FSA approved***
						</li>
					</ul>
					<button
						type="button"
						class="btn btn-black btn-block glp1-cta"
						@click="handleCtaClick($event, 'ButtonClick', 'CHECK_MY_ELIGIBILITY', 'CHECK_MY_ELIGIBILITY_TOP')"
					>
						See if I qualify
					</button>
				</div>

				<img class="glp1-hero__image" :src="heroPng" alt="" />
			</section>

			<BlockVisibilityObserver title="LANDING_MEDICATION_TYPE" :threshold="1">
				<section class="glp1-products">
					<div class="glp1-pill glp1-pill--green">
						<img src="@/assets/ds_shield.svg" class="glp1-pill__icon" />
						FSA/HSA eligible
					</div>
					<h2 class="glp1-section-title">
						<span class="glp1-gradient-text">Doctor-trusted</span>
						weight loss medications, tailored to you
					</h2>
					<p class="glp1-section-text">
						Find the right GLP-1 option with confidence knowing it’s prescribed by a licensed clinician and designed to
						fit your budget.
					</p>
					<div class="glp1-products__carousel">
						<Carousel
							:slides="productSlides"
							:slides-to-show="1.08"
							analytics-content-type="glp1-product-cards"
							analytics-placement="GLP1Landing"
							@after-change="onProductSlideChange"
							v-slot="{ slide }"
						>
							<div class="glp1-products__slide">
								<div class="glp1-product-card">
									<img class="glp1-product-card__image" :src="slide.image" alt="" />
									<div class="glp1-product-card__content">
										<p class="glp1-product-card__price">{{ slide.price }}</p>
										<h3 class="glp1-product-card__title">{{ slide.title }}</h3>
										<p class="glp1-product-card__subtitle">{{ slide.subtitle }}</p>
									</div>
								</div>
							</div>
						</Carousel>
					</div>
					<button
						type="button"
						class="btn btn-black btn-block glp1-cta"
						@click="
							handleCtaClick($event, 'ButtonClick', 'GET_STARTED', 'GET_STARTED_MAIN', {
								selected_medication_type: selectedMedicationType,
							})
						"
					>
						See if I qualify
					</button>
				</section>
			</BlockVisibilityObserver>

			<BlockVisibilityObserver title="LANDING_HOW_DOES_IT_WORK" :threshold="1">
				<section class="glp1-how">
					<div class="glp1-pill">How does it work?</div>
					<h2 class="glp1-section-title">Everything you need is included</h2>
					<div class="glp1-steps">
						<div class="glp1-step">
							<div class="glp1-step__icon">
								<inline-svg :src="step1Svg" aria-hidden="true" />
							</div>
							<div class="glp1-step__body">
								<div class="glp1-step__header">
									<h3>Intake survey</h3>
									<span class="glp1-step__badge">Now</span>
								</div>
								<p>Share details about your health history, habits, and goals through our secure online platform.</p>
							</div>
						</div>
						<div class="glp1-step">
							<div class="glp1-step__icon">
								<inline-svg :src="step2Svg" aria-hidden="true" />
							</div>
							<div class="glp1-step__body">
								<h3>Clinician review</h3>
								<p>A clinician reviews your intake—fully online—to determine the best treatment option for you.</p>
							</div>
						</div>
						<div class="glp1-step">
							<div class="glp1-step__icon">
								<inline-svg :src="step3Svg" aria-hidden="true" />
							</div>
							<div class="glp1-step__body">
								<h3>Start losing weight</h3>
								<p>
									Get medication shipped to your door, if eligible, and start losing weight with your personalized plan.
								</p>
							</div>
						</div>
						<div class="glp1-step">
							<div class="glp1-step__icon">
								<inline-svg :src="step4Svg" aria-hidden="true" />
							</div>
							<div class="glp1-step__body">
								<h3>Get ongoing support</h3>
								<p>Connect with your clinician 24/7 for dose adjustments and answers to questions—100% online.</p>
							</div>
						</div>
					</div>
					<button
						type="button"
						class="btn btn-black btn-block glp1-cta"
						@click="handleCtaClick($event, 'ButtonClick', 'START_INTAKE_SURVEY')"
					>
						Start intake survey
					</button>
				</section>
			</BlockVisibilityObserver>

			<BlockVisibilityObserver title="LANDING_BENEFITS" :threshold="1">
				<section class="glp1-compare">
					<h2 class="glp1-section-title">
						<span class="glp1-gradient-text-reverse">Weight loss that lasts</span>
						with personalized care and GLP-1 medications
					</h2>
					<p class="glp1-section-text">
						Simple provides tailored medication and behavior change so that even if your med journey ends, your progress
						doesn't.
					</p>
					<div class="glp1-compare__table">
						<div class="glp1-compare__row glp1-compare__header">
							<div></div>
							<div class="glp1-compare__logo">Meds only</div>
							<inline-svg :src="logoSvg" class="glp1-compare__logo glp1-compare__logo--simple" aria-label="Simple" />
						</div>
						<div class="glp1-compare__row">
							<p>Free home delivery of prescribed weight-loss medication*</p>
							<inline-svg :src="dsCloseSvg" class="glp1-compare__icon glp1-compare__icon--close" />
							<inline-svg :src="dsCheckSvg" class="glp1-compare__icon glp1-compare__icon--check" />
						</div>
						<div class="glp1-compare__row">
							<p>Access to licensed clinicians—100% online</p>
							<inline-svg :src="dsCloseSvg" class="glp1-compare__icon glp1-compare__icon--close" />
							<inline-svg :src="dsCheckSvg" class="glp1-compare__icon glp1-compare__icon--check" />
						</div>
						<div class="glp1-compare__row">
							<p>Maintain muscle with our Muscle Support program</p>
							<inline-svg :src="dsCloseSvg" class="glp1-compare__icon glp1-compare__icon--close" />
							<inline-svg :src="dsCheckSvg" class="glp1-compare__icon glp1-compare__icon--check" />
						</div>
						<div class="glp1-compare__row">
							<p>Tools to build sustainable habits and monitor progress</p>
							<inline-svg :src="dsCloseSvg" class="glp1-compare__icon glp1-compare__icon--close" />
							<inline-svg :src="dsCheckSvg" class="glp1-compare__icon glp1-compare__icon--check" />
						</div>
						<div class="glp1-compare__row">
							<p>Access to coaching support</p>
							<inline-svg :src="dsCloseSvg" class="glp1-compare__icon glp1-compare__icon--close" />
							<inline-svg :src="dsCheckSvg" class="glp1-compare__icon glp1-compare__icon--check" />
						</div>
						<div class="glp1-compare__row">
							<p>Dosing tailored to minimize side effects</p>
							<inline-svg :src="dsCloseSvg" class="glp1-compare__icon glp1-compare__icon--close" />
							<inline-svg :src="dsCheckSvg" class="glp1-compare__icon glp1-compare__icon--check" />
						</div>
					</div>
					<button
						type="button"
						class="btn btn-black btn-block glp1-cta"
						@click="handleCtaClick($event, 'ButtonClick', 'GET_STARTED', 'GET_STARTED_BOTTOM')"
					>
						Get started
					</button>
				</section>
			</BlockVisibilityObserver>

			<BlockVisibilityObserver title="LANDING_FAQ" :threshold="1">
				<section class="glp1-faq">
					<div class="glp1-pill">FAQ</div>
					<h2 class="glp1-section-title">Got a question?</h2>
					<Accordion class="glp1-faq__accordion" :items="faqItems" />
				</section>
			</BlockVisibilityObserver>

			<BlockVisibilityObserver title="LANDING_ABOUT_COMPANY" :threshold="1">
				<section class="glp1-video">
					<video class="glp1-video__media" autoplay loop muted playsinline>
						<source src="@/assets/landing-glp1/promo.mp4" autoplay loop muted playsinline />
					</video>
					<div class="glp1-video__overlay">
						<h2 class="glp1-section-title glp1-section-title--inverse">A weight loss plan as unique as you are</h2>
						<p class="glp1-section-text glp1-section-text--inverse">
							A licensed provider will review your information and build a treatment plan around your body — from
							medication to personalized dosing and ongoing support.
						</p>
						<button
							type="button"
							class="btn btn-white btn-block glp1-cta glp1-cta--light"
							@click="handleCtaClick($event, 'ButtonClick', 'CHECK_MY_ELIGIBILITY', 'CHECK_MY_ELIGIBILITY_BOTTOM')"
						>
							See if I qualify
						</button>
					</div>
				</section>
			</BlockVisibilityObserver>
		</main>

		<footer class="glp1-footer">
			<inline-svg :src="logoWhiteInlineSvg" class="glp1-footer__logo" aria-label="Simple" />
			<div class="glp1-footer__links">
				<div @click="handleCtaClick($event, 'ButtonClick', 'JOIN_SIMPLE')">Join Simple</div>
				<a
					href="https://help.simple.life/"
					target="_blank"
					rel="noopener"
					@click="logEvent('ButtonClick', 'HELP_CENTER')"
				>
					Help Center
				</a>
				<a
					href="https://simple.life/tos"
					target="_blank"
					rel="noopener"
					@click="logEvent('ButtonClick', 'TERMS_OF_USE')"
				>
					Terms of Use
				</a>
				<a
					href="https://simple.life/privacy"
					target="_blank"
					rel="noopener"
					@click="logEvent('ButtonClick', 'PRIVACY_POLICY')"
				>
					Privacy Policy
				</a>
				<a
					href="https://simple.life/refund"
					target="_blank"
					rel="noopener"
					@click="logEvent('ButtonClick', 'REFUND_POLICY')"
				>
					Refund Policy
				</a>
			</div>
			<p class="glp1-footer__copyright">© 2026 Simple. All rights reserved.</p>
		</footer>

		<section class="glp1-disclaimer">
			<BlockVisibilityObserver title="LANDING_DISCLAIMER_TOP" :threshold="1">
				<p>
					* Eligibility for prescription will be determined by a clinician. If prescribed, medication may be compounded
					with additives at clinician’s discretion. Simple does not provide medical advice.
				</p>
			</BlockVisibilityObserver>
			<p>
				** Licensed clinicians are provided through our external medical providers. They may prescribe — or choose not
				to prescribe — GLP-1 medications based on their independent professional judgment.
			</p>
			<p>*** FSA/HSA eligibility and coverage varies by plan provider.</p>
			<p>Important Safety Information for Certain Weight Management Medications</p>
			<p>
				This information is intended to support informed discussions between users and their prescribing clinicians. It
				does not replace medical advice. Decisions about whether a medication is appropriate should always be made by a
				qualified healthcare professional.
			</p>
			<p class="mb-0">Compounded Semaglutide</p>
			<p class="mb-0">Key safety considerations</p>
			<p class="mb-0">
				Compounded semaglutide products are not approved by the U.S. Food and Drug Administration (FDA) and have not
				undergone FDA review for safety, quality, or efficacy. In some cases, an independent clinician may prescribe a
				compounded version, for example to manage supply limitations or for other clinically appropriate reasons. Simple
				Life does not provide medical care, and medication decisions are made solely by the prescribing clinician during
				clinical consultations.
			</p>
			<p class="mb-0">
				In animal studies, semaglutide has been associated with thyroid C-cell tumors in a manner related to dose and
				duration of exposure. It is not known whether these findings translate to humans, including whether semaglutide
				increases the risk of medullary thyroid carcinoma (MTC). As a precaution, compounded semaglutide should not be
				used by individuals with a personal or family history of MTC or with Multiple Endocrine Neoplasia syndrome type
				2 (MEN 2).
			</p>
			<p>
				Frequently reported side effects include nausea, vomiting, diarrhea, abdominal discomfort, and constipation.
				Patients should report any persistent or concerning side effects to their clinician. This summary does not
				include all possible risks; additional information can be obtained from the dispensing pharmacy.
			</p>
			<p class="mb-0">Ozempic® (semaglutide)</p>
			<p class="mb-0">Key safety considerations</p>
			<p class="mb-0">
				Studies in rodents have shown that semaglutide can cause thyroid C-cell tumors with increasing dose and
				duration. The relevance of these findings to humans is unknown. Ozempic is contraindicated in individuals with a
				personal or family history of medullary thyroid carcinoma or with MEN 2.
			</p>
			<p>
				Common side effects may include nausea, vomiting, diarrhea, abdominal pain, and constipation. Ozempic is not
				approved by the FDA specifically for weight loss; however, a clinician may prescribe it off-label for weight
				management when clinically appropriate. Patients should discuss potential risks and benefits with their
				clinician and report any side effects that are bothersome or persistent. For full prescribing information, refer
				to the manufacturer’s documentation.
			</p>
			<p class="mb-0">Wegovy® (semaglutide)</p>
			<p class="mb-0">Key safety considerations</p>
			<p class="mb-0">
				In animal studies, semaglutide has been linked to thyroid C-cell tumors in a dose- and duration-dependent
				manner. It is not known whether Wegovy carries the same risk in humans. Wegovy should not be used by individuals
				with a personal or family history of MTC or with MEN 2.
			</p>
			<p>
				Commonly reported side effects include nausea, vomiting, diarrhea, constipation, abdominal pain, headache,
				fatigue, dizziness, bloating, gas, and heartburn. Patients should consult their clinician about any symptoms
				that are severe, persistent, or concerning. This overview does not capture all potential side effects; full
				prescribing information is available from the manufacturer.
			</p>
			<p class="mb-0">Zepbound® (tirzepatide)</p>
			<p class="mb-0">Key safety considerations</p>
			<p class="mb-0">
				Zepbound may increase the risk of thyroid tumors, including thyroid cancer. Patients should seek medical advice
				promptly if they notice symptoms such as a lump in the neck, swelling, hoarseness, difficulty swallowing, or
				shortness of breath.
			</p>
			<p class="mb-0">Zepbound should not be used by individuals who:</p>
			<ul class="mb-0">
				<li>Have a personal or family history of medullary thyroid carcinoma</li>
				<li>Have Multiple Endocrine Neoplasia syndrome type 2</li>
				<li>Have experienced a serious allergic reaction to tirzepatide or any of its components</li>
			</ul>
			<p>
				Frequently reported side effects include nausea, vomiting, diarrhea, constipation, abdominal pain, indigestion,
				injection-site reactions, fatigue, allergic reactions, belching, hair loss, and heartburn. This is not a
				complete list of possible side effects. Patients should discuss any ongoing or concerning symptoms with their
				healthcare provider. Full prescribing information is available from the manufacturer.
			</p>
			<BlockVisibilityObserver title="LANDING_DISCLAIMER_BOTTOM" :threshold="1">
				<p>
					This document provides a high-level summary and does not include all possible warnings, precautions, or side
					effects. Patients should always consult their prescribing clinician and review official prescribing
					information before starting or continuing any medication.
				</p>
			</BlockVisibilityObserver>
		</section>
	</div>
</template>

<script>
import { mapState } from 'pinia'
import { useMainStore } from '@/store/main'
import { useSurveyStore } from '@/store/survey'
import textInterpolations from '@/modules/Onboarding/text-interpolation.js'
import Accordion from '@/components/Accordion.vue'
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import Carousel from '@/components/common/Carousel.vue'
import glpInjectionPng from '@/assets/landing-glp1/glp-injection.png'
import glpTabletPng from '@/assets/landing-glp1/glp-tablet.png'
import logoSvg from '@/assets/landing-glp1/logo.svg'
import checkSvg from '@/assets/landing-glp1/check.svg'
import heroPng from '@/assets/landing-glp1/hero.png'
import step1Svg from '@/assets/landing-glp1/step-1.svg'
import step2Svg from '@/assets/landing-glp1/step-2.svg'
import step3Svg from '@/assets/landing-glp1/step-3.svg'
import step4Svg from '@/assets/landing-glp1/step-4.svg'
import dsCloseSvg from '@/assets/icons/ds_close.svg'
import dsCheckSvg from '@/assets/icons/ds_check.svg'
import logoWhiteInlineSvg from '@/assets/logos/logo-white-inline.svg'

export default {
	name: 'LandingGlp1',
	components: { Carousel, BlockVisibilityObserver, Accordion },
	emits: ['progressLoaderCompleted'],
	setup() {
		return {
			logoSvg,
			checkSvg,
			heroPng,
			step1Svg,
			step2Svg,
			step3Svg,
			step4Svg,
			dsCloseSvg,
			dsCheckSvg,
			logoWhiteInlineSvg,
		}
	},

	data() {
		return {
			productSlides: [
				{
					id: 'glp-injections',
					image: glpInjectionPng,
					price: 'Starting at $99',
					title: 'GLP-1 Injections',
					subtitle: 'One injection per week',
				},
				{
					id: 'glp-tablets',
					image: glpTabletPng,
					price: 'Starting at $99',
					title: 'GLP-1 Tablets',
					subtitle: 'One dissolvable tablet per day',
				},
			],

			faqItems: [
				{
					title: 'How much does the program cost?',
					content:
						'The Simple GLP-1 program starts at $99 for your first month. This includes a review by a licensed clinician, a personalized plan, ongoing guidance, and prescribed medication shipped to your door, if approved. After that, there are flexible plan options that include continued clinical support and program access.',
				},
				{
					title: 'Will this work for me?',
					content:
						'Everyone’s body is different. That’s why licensed clinicians review your health information to determine whether a GLP-1 medication may be appropriate for you. If prescribed, Simple combines medication with personalized guidance and behavior support to help you work toward your weight-loss goals.',
				},
				{
					title: "What if my insurance doesn't cover the medication?",
					content:
						'Simple programs are <span class="accordion__text-bold">cash-pay</span>, so insurance is not required. Some users may choose to submit receipts for reimbursement depending on their plan, but coverage is not guaranteed. We aim to keep pricing transparent and accessible while supporting your long-term weight-loss goals.',
				},
				{
					title: 'What can I expect after I sign up?',
					content:
						'After completing your health intake, a licensed clinician will review your information to determine whether treatment may be appropriate. If approved, your prescribed medication will be prepared by a pharmacy and shipped directly to your door, along with access to your personalized program.',
				},
			],

			selectedProductId: 'glp-injections',
		}
	},

	computed: {
		...mapState(useMainStore, {
			weightUnitSystem: 'getWeightUnitSystem',
		}),

		selectedMedicationType() {
			return this.selectedProductId === 'glp-tablets' ? 'TABLETS' : 'INJECTIONS'
		},

		weightLossOptions() {
			const interp = textInterpolations({ weightUnitSystem: this.weightUnitSystem }, this.$t)
			return [
				{ label: `Lose ${interp.weightLossGoalsFirstOption()}`, value: '1_10_kg' },
				{ label: `Lose ${interp.weightLossGoalsSecondOption()}`, value: '11_20_kg' },
				{ label: `Lose over ${interp.weightLossGoalsThirdOption()}`, value: 'over_20_kg' },
				{ label: 'I haven’t decided yet', value: 'not_sure' },
			]
		},
	},

	methods: {
		handleCtaClick(event, eventName = 'ButtonClick', title = '', type = '', extraParams = {}) {
			this.logEvent(eventName, title, type, extraParams)
			this.$emit('progressLoaderCompleted')
		},

		logEvent(event, title = '', type = '', extraParams = {}) {
			const params = { ...extraParams }
			if (title) params.title = title
			if (type) params.type = type

			this.$analytic.logEvent(event, params)

			if (event === 'ButtonClick') {
				this.$analytic.logEvent('OnboardingMedWelcomeScreenDone')
			}
		},

		handleWeightLossAnswer(answer) {
			useSurveyStore().updateProperty({ property: 'weight_loss_goals', value: [answer], isData: true })
			this.$analytic.logEvent('OnboardingMedWelcomeScreenDone', { data: [answer] })
			this.$emit('progressLoaderCompleted')
		},

		onProductSlideChange(index) {
			const slide = this.productSlides[index]

			if (slide?.id) {
				this.selectedProductId = slide.id
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.glp1 {
	background: var(--Background-Primary);
	color: var(--Text-Primary-100);
	//	фикс для container-fluid
	margin-left: calc(var(--size-32) * -1);
	margin-right: calc(var(--size-32) * -1);
}

.glp1__nav {
	background: linear-gradient(180deg, var(--Fill-White) 60%, transparent 100%);
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: var(--size-12) var(--size-16);
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 10;
	padding-bottom: var(--size-36);
}

.glp1__logo {
	height: var(--size-16);
	width: auto;
}

.glp1__content {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
	padding-top: calc(var(--size-12) + var(--size-36) + var(--size-36));
	margin-bottom: var(--size-72);
}

.glp1-hero {
	background: linear-gradient(180deg, #fbeded 0%, #e8d2f1 100%); // Custom gradient from Figma, not in design system
	border-radius: var(--size-32);
	position: relative;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-32);
	margin-bottom: var(--size-64);
}

.glp1-hero__visual {
	height: 309px; // Custom size from Figma, not in design system
	position: relative;
	width: 100%;
}

.glp1-hero__image {
	width: 100%;
}

.glp1-hero__phone-screen {
	border-radius: var(--size-20);
	width: 100%;
	display: block;
}

.glp1-hero__vial {
	position: absolute;
	left: var(--size-24);
	top: var(--size-32);
	width: 52%;
}

.glp1-hero__copy {
	display: flex;
	flex-direction: column;
	gap: var(--size-20);
	text-align: center;
	width: 100%;
	padding: var(--size-40) var(--size-12) 0;
}

.glp1-pill {
	background: linear-gradient(90deg, #ead4f1 0%, #f9d2e9 100%); // Custom gradient from Figma, not in design system
	border-radius: var(--size-80);
	color: var(--Text-Primary-100);
	font-size: var(--size-14);
	font-weight: 600;
	line-height: var(--size-20);
	padding: var(--size-12) var(--size-20);
	display: inline-flex;
	align-items: center;
	gap: var(--size-6);
	justify-content: center;
	align-self: center;
}

.glp1-pill--green {
	background: linear-gradient(270deg, #dcffc4 0%, #c3fcd4 96.56%); // Custom gradient from Figma, not in design system
}

.glp1-pill__icon {
	width: var(--size-20);
	height: var(--size-20);
}

.glp1-hero__title {
	font-size: var(--size-28);
	font-weight: 600;
	line-height: var(--size-32);
	letter-spacing: -1.8px;
	margin: 0;
	padding-inline: var(--size-20);
}

.glp1-gradient-text {
	background: linear-gradient(
		90deg,
		var(--Fill-Violet) 0%,
		#ff3ed2 100%
	); // Custom gradient from Figma, not in design system
	-webkit-background-clip: text;
	background-clip: text;
	color: transparent;
}

.glp1-gradient-text-reverse {
	background: linear-gradient(
		90deg,
		#ff3ed2 0%,
		var(--Fill-Violet) 100%
	); // Custom gradient from Figma, not in design system
	-webkit-background-clip: text;
	background-clip: text;
	color: transparent;
}

.glp1-hero__list {
	list-style: none;
	padding: 0;
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: var(--size-12);
}

.glp1-hero__item {
	display: flex;
	align-items: center;
	gap: var(--size-8);
	font-size: var(--size-16);
	line-height: var(--size-20);
	letter-spacing: -0.4px;
	font-weight: 500;
	text-align: left;
}

.glp1-hero__check {
	background: var(--Fill-Inverse);
	border-radius: 50%;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--size-16);
	height: var(--size-16);
}

.glp1-hero__check svg {
	width: var(--size-8);
	height: var(--size-8);
}

.glp1-cta {
	font-size: var(--size-16);
	font-weight: 600;
	line-height: var(--size-18);
	padding: var(--size-16) var(--size-20);
	border-radius: var(--size-20);
}

.glp1-cta--light {
	border-color: var(--Fill-White);
}

.glp1-cta--nav {
	font-size: var(--size-12);
	line-height: var(--size-16);
	padding: var(--size-10) var(--size-14);
	border-radius: var(--size-14);
}

.glp1-products {
	display: flex;
	flex-direction: column;
	gap: var(--size-20);
	align-items: center;
	text-align: center;
	margin-bottom: var(--size-64);
}

.glp1-section-title {
	font-size: var(--size-28);
	font-weight: 600;
	line-height: var(--size-32);
	letter-spacing: -1.8px;
	margin: 0;
	text-align: center;
}

.glp1-section-title--inverse {
	color: var(--Text-On-Color);
}

.glp1-section-text {
	color: var(--Text-Primary-60);
	font-size: var(--size-16);
	line-height: var(--size-20);
	letter-spacing: -0.4px;
	margin: 0;
	text-align: center;
	font-weight: 500;
}

.glp1-section-text--inverse {
	color: var(--Text-On-Color);
	font-weight: 500;
}

.glp1-products__carousel {
	width: calc(100% + var(--size-40));
	padding-left: var(--size-12);
}

.glp1-product-card {
	background: linear-gradient(180deg, #fbedee 0%, #f8effe 100%); // Custom gradient from Figma, not in design system
	border-radius: var(--size-32);
	padding: var(--size-12);
	position: relative;
	overflow: hidden;
	padding-bottom: var(--size-32);
}

.glp1-product-card__image {
	max-height: 272px;
	margin: auto;
	display: block;
}

.glp1-product-card__content {
	text-align: center;
	margin-top: var(--size-12);
}

.glp1-product-card__price {
	color: var(--Text-Violet);
	font-weight: 600;
	font-size: var(--size-16);
	line-height: var(--size-20);
	letter-spacing: -0.4px;
	margin: 0 0 var(--size-8);
}

.glp1-product-card__title {
	font-size: var(--size-24);
	line-height: var(--size-28);
	margin: 0 0 var(--size-8);
}

.glp1-product-card__subtitle {
	color: var(--Text-Primary-60);
	font-size: var(--size-16);
	line-height: var(--size-20);
	margin: 0;
}

.glp1-products__slide {
	padding: 0 var(--size-8);
}

.glp1-how {
	background: #fdf6ff; // Custom color from Figma, not in design system
	border-radius: var(--size-32);
	padding: var(--size-12);
	padding-top: var(--size-40);
	display: flex;
	flex-direction: column;
	gap: var(--size-20);
	align-items: center;
	text-align: center;
}

.glp1-steps {
	display: flex;
	flex-direction: column;
	gap: var(--size-28);
	width: 100%;
	position: relative;

	&::before {
		content: '';
		display: block;
		width: 0;
		border-left: 1px dashed #f0e2f4;
		height: 100%;
		position: absolute;
		left: var(--size-20);
	}
}

.glp1-step {
	display: flex;
	gap: var(--size-12);
	text-align: left;

	&:last-of-type .glp1-step__icon {
		background-color: #fdf6ff;
	}
}

.glp1-step__icon {
	z-index: 1;
}

.glp1-step__icon :deep(svg) {
	display: block;
	width: var(--size-40);
	height: var(--size-40);
}

.glp1-step__body h3 {
	font-size: var(--size-18);
	margin: 0 0 var(--size-8);
	line-height: var(--size-22);
}

.glp1-step__body p {
	font-weight: 500;
	font-size: var(--size-16);
	color: var(--Text-Primary-60);
	line-height: var(--size-20);
	margin: 0;
}

.glp1-step__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--size-8);
}

.glp1-step__badge {
	background: #d9ffc6; // Custom color from Figma, not in design system
	border-radius: var(--size-80);
	font-size: var(--size-12);
	font-weight: 600;
	line-height: var(--size-16);
	padding: var(--size-4) var(--size-8);
}

.glp1-compare {
	background: var(--Fill-Base);
	border-radius: var(--size-32);
	padding: var(--size-12);
	padding-top: var(--size-40);
	display: flex;
	flex-direction: column;
	gap: var(--size-20);
}

.glp1-compare__table {
	background:
		linear-gradient(
			90deg,
			var(--Fill-White) 0%,
			var(--Fill-White) calc(100% - var(--size-88)),
			transparent calc(100% - var(--size-88)),
			transparent 100%
		),
		linear-gradient(180deg, #faecf6 0%, #f1d8fc 100%);
	border-radius: var(--size-20);
	box-shadow: 0 1px 2px rgba(20, 21, 26, 0.05);
	overflow: hidden;
	position: relative;
}

.glp1-compare__row {
	display: grid;
	grid-template-columns: 1fr var(--size-40) var(--size-60);
	gap: var(--size-16);
	padding: var(--size-16) var(--size-20);
	border-bottom: 1px solid var(--Stroke-Secondary);
	align-items: center;
}

.glp1-compare__row:last-child {
	border-bottom: none;
}

.glp1-compare__row p {
	font-size: var(--size-14);
	line-height: var(--size-20);
	margin: 0;
	font-weight: 500;
}

.glp1-compare__header {
	font-weight: 700;
}

.glp1-compare__logo {
	font-size: var(--size-12);
	font-weight: 700;
	text-align: center;
}

.glp1-compare__logo--simple {
	color: var(--Text-Primary-100);
	height: var(--size-14);
	width: var(--size-60);
}

.glp1-compare__icon {
	margin: auto;
}

.glp1-compare__icon--close {
	color: var(--Text-Primary-40);
	width: var(--size-18);
	height: var(--size-18);
}

.glp1-compare__icon--check {
	color: var(--Text-Violet);
	width: var(--size-24);
	height: var(--size-24);
}

.glp1-faq {
	background: rgba(246, 212, 255, 0.2); // Custom color from Figma, not in design system
	border-radius: var(--size-24);
	padding: var(--size-40) var(--size-8) var(--size-8);
	display: flex;
	flex-direction: column;
	gap: var(--size-20);
	align-items: center;
	text-align: center;
}

.glp1-faq__accordion {
	width: 100%;
}

.glp1-video {
	border-radius: var(--size-32);
	overflow: hidden;
	position: relative;
	height: 373px; // Custom size from Figma, not in design system
}

.glp1-video__media {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

.glp1-video__overlay {
	position: absolute;
	inset: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	align-items: center;
	text-align: center;
	gap: var(--size-16);
	padding: var(--size-16);
}

.glp1-footer {
	background: #232c33; // Custom color from Figma, not in design system
	color: var(--Text-On-Color);
	padding: var(--size-40) var(--size-16);
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
}

.glp1-footer__logo {
	width: var(--size-100);
}

.glp1-footer__links {
	display: flex;
	flex-direction: column;
	gap: var(--size-20);
	font-weight: 600;
}

.glp1-footer__links a {
	color: var(--Text-On-Color);
	text-decoration: none;
}

.glp1-footer__copyright {
	color: var(--Text-White-60);
	font-size: var(--size-12);
	line-height: var(--size-16);
	margin: 0;
}

.glp1-disclaimer {
	padding: var(--size-32) var(--size-20) 0;
	font-size: var(--size-12);
	line-height: var(--size-16);
	color: var(--Text-Primary-60);
}

.glp1-disclaimer ul {
	padding-left: var(--size-20);
	margin: 0;
}

:deep(.agile__dot button) {
	transition: 0.3s ease-in-out;
	transition-property: width, background-color, border-radius;
}

:deep(.agile__dot--current button) {
	background-color: #232c33;
	border-radius: var(--size-18);
	width: var(--size-16);
}

.btn-black {
	background: #232c33;
}

.glp1-quiz {
	background: #f6edf9; // Custom color from Figma, not in design system
	border-radius: var(--size-32);
	padding: var(--size-40) var(--size-12);
	display: flex;
	flex-direction: column;
	gap: var(--size-20);
	align-items: center;
	text-align: center;
}

.glp1-quiz__subtitle {
	font-size: var(--size-20);
	font-weight: 600;
	line-height: var(--size-24);
	letter-spacing: -0.75px; // Spacing/Subtitle Height token
	margin: 0;
	width: 293px;
}

.glp1-quiz__title {
	font-size: var(--size-28);
	font-weight: 600;
	line-height: var(--size-32);
	letter-spacing: -1.8px;
	margin: 0;
	color: var(--Text-Primary-100);
}

.glp1-quiz__options {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
	width: 100%;
}

.glp1-quiz__option {
	background: var(--Fill-Primary);
	border: none;
	border-radius: var(--size-16);
	cursor: pointer;
	font-size: var(--size-16);
	font-weight: 600;
	height: var(--size-64);
	line-height: var(--size-20);
	padding: var(--size-20) var(--size-16);
	text-align: left;
	width: 100%;
	color: var(--Text-Primary-100);

	&:active {
		opacity: 0.8;
	}
}
</style>
